/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { getContexts } from "./contexts";
import { eventDefinitions } from "./event-definitions";
import { Snowplow } from "./Snowplow";
import type { EventDefinition, TrackingProps } from "./types";

export interface SnowplowContextInterface {
  config: TrackingProps;
  snowplow?: Snowplow;
}

const SnowplowContext = createContext<SnowplowContextInterface | null>(null);

type ProviderProps = {
  scripts: TrackingProps;
  children: ReactNode;
};

export const SnowplowProvider = ({ scripts, children }: ProviderProps) => {
  const [config, _setConfig] = useState<TrackingProps>(scripts);
  const [snowplowInstance] = useState<Snowplow>(() =>
    Snowplow.getInstance(config),
  );

  // Attach event handlers and set contexts
  useEffect(() => {
    if (snowplowInstance && scripts) {
      const contexts = getContexts(config);

      snowplowInstance.setContexts(contexts).addEventHandlers(eventDefinitions);
      // Send page view event
      if (config.trackPageView) {
        void snowplowInstance.trackView();
      }
    }
  }, [config, snowplowInstance, scripts]);

  const value: SnowplowContextInterface = useMemo(
    () => ({
      config,
      snowplow: snowplowInstance,
    }),
    [config, snowplowInstance],
  );

  return (
    <SnowplowContext.Provider value={value}>
      {children}
    </SnowplowContext.Provider>
  );
};

export function useSnowplowContext() {
  const context = useContext(SnowplowContext);

  if (!context) {
    throw new Error(
      "useSnowplowContext must be used inside a `SnowplowProvider`",
    );
  }
  return context;
}
